import { Spot, SpotCategory } from '../types/spot';
import { KakaoMapsApi, KakaoPlaceItem } from './kakaoMaps';
import { getCuratedAuthenticPhoto } from './imageResolver';

// Curated aesthetic fallback photography by category
const KAKAO_CATEGORY_PHOTOS: Record<string, string> = {
  vegetarian_food: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=1000&auto=format&fit=crop&q=85',
  cafe: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1000&auto=format&fit=crop&q=85',
  restaurant: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1000&auto=format&fit=crop&q=85',
  nature_viewpoint: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1000&auto=format&fit=crop&q=85',
  night_view: 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=1000&auto=format&fit=crop&q=85',
  photo_spot: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1000&auto=format&fit=crop&q=85',
  palace_heritage: 'https://images.unsplash.com/photo-1548115184-bc6544d06a58?w=1000&auto=format&fit=crop&q=85',
  activity: 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1000&auto=format&fit=crop&q=85',
  cultural_hidden: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1000&auto=format&fit=crop&q=85',
  default: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1000&auto=format&fit=crop&q=85',
};

/**
 * Searches real places in Korea directly from Kakao's official database using Kakao Maps SDK Places service.
 * Performs both category (AT4: Tourist Attraction, CT1: Cultural Facility, FD6: Restaurant, CE7: Cafe)
 * and keyword searches (including vegetarian/vegan food & local sights) around the user's location.
 */
export async function fetchKakaoLivePlaces(
  kakaoApi: KakaoMapsApi,
  lat: number,
  lng: number,
  radiusMeters: number = 10000,
  targetCategory?: string
): Promise<Spot[]> {
  try {
    const places = new kakaoApi.maps.services.Places();
    const loc = new kakaoApi.maps.LatLng(lat, lng);
    const safeRadius = Math.min(25000, Math.max(500, radiusMeters));

    // Category searches
    // AT4: Tourist Attraction, CT1: Cultural Facility, FD6: Restaurant, CE7: Cafe
    const categoryCodes = targetCategory === 'vegetarian_food'
      ? ['FD6', 'CE7']
      : ['AT4', 'CT1', 'FD6', 'CE7'];

    const categoryPromises = categoryCodes.map((catCode) => {
      return new Promise<KakaoPlaceItem[]>((resolve) => {
        places.categorySearch(
          catCode,
          (result, status) => {
            if (status === kakaoApi.maps.services.Status.OK && Array.isArray(result)) {
              resolve(result);
            } else {
              resolve([]);
            }
          },
          {
            location: loc,
            radius: safeRadius,
            size: 15,
          }
        );
      });
    });

    // Keyword searches: search for popular sights and vegetarian/vegan dining
    const keywords = targetCategory === 'vegetarian_food'
      ? ['비건', '채식', '샐러드', '사찰음식', '베지테리언']
      : ['관광명소', '전망대', '공원', '비건', '채식'];

    const keywordPromises = keywords.map((kw) => {
      return new Promise<KakaoPlaceItem[]>((resolve) => {
        places.keywordSearch(
          kw,
          (result, status) => {
            if (status === kakaoApi.maps.services.Status.OK && Array.isArray(result)) {
              resolve(result);
            } else {
              resolve([]);
            }
          },
          {
            location: loc,
            radius: safeRadius,
            size: 10,
          }
        );
      });
    });

    const settled = await Promise.all([...categoryPromises, ...keywordPromises]);
    const combined = settled.flat();

    const seenIds = new Set<string>();
    const spots: Spot[] = [];

    // Is current location on Jeju Island?
    const isJejuCoords = lat >= 33.1 && lat <= 34.0 && lng >= 126.1 && lng <= 127.0;

    for (const item of combined) {
      if (seenIds.has(item.id)) continue;
      seenIds.add(item.id);

      const spotLat = parseFloat(item.y);
      const spotLng = parseFloat(item.x);
      if (isNaN(spotLat) || isNaN(spotLng)) continue;

      const catName = item.category_name || '';
      const placeName = item.place_name || '';

      // Determine Category
      let category: SpotCategory = 'photo_spot';
      let isVeggie = false;
      let dietDetailsDe = '';
      let dietDetailsEn = '';

      if (
        catName.includes('채식') ||
        catName.includes('비건') ||
        catName.includes('샐러드') ||
        catName.includes('사찰음식') ||
        placeName.includes('비건') ||
        placeName.includes('채식') ||
        placeName.includes('샐러드') ||
        placeName.includes('VEGAN') ||
        placeName.includes('Vegan')
      ) {
        category = 'vegetarian_food';
        isVeggie = true;
        dietDetailsDe = 'Offizieller Kakao-Spot: Vegetarische & vegane Optionen / Speisen vor Ort.';
        dietDetailsEn = 'Official Kakao Place: Vegetarian & vegan options available on site.';
      } else if (item.category_group_code === 'FD6') {
        category = 'vegetarian_food';
      } else if (item.category_group_code === 'CE7') {
        category = 'photo_spot';
      } else if (item.category_group_code === 'CT1') {
        category = 'palace_heritage';
      } else if (
        item.category_group_code === 'MT1' ||
        catName.includes('쇼핑') ||
        catName.includes('백화점') ||
        catName.includes('아울렛') ||
        catName.includes('몰') ||
        catName.includes('약국') ||
        placeName.includes('약국') ||
        placeName.includes('몰')
      ) {
        category = 'shopping';
      } else if (
        catName.includes('레저') ||
        catName.includes('스포츠') ||
        catName.includes('골프') ||
        catName.includes('볼링') ||
        catName.includes('액티비티') ||
        catName.includes('방탈출')
      ) {
        category = 'activity';
      } else if (catName.includes('공원') || catName.includes('전망') || catName.includes('자연') || catName.includes('해변') || catName.includes('오름')) {
        category = 'nature_viewpoint';
      } else if (catName.includes('야경')) {
        category = 'night_view';
      }

      // Determine Region
      let region: Spot['region'] = 'Other';
      const addr = item.address_name || '';
      if (isJejuCoords || addr.includes('제주') || (item.road_address_name && item.road_address_name.includes('제주'))) {
        region = 'Jeju';
      } else if (addr.includes('서울')) {
        region = 'Seoul';
      } else if (addr.includes('부산')) {
        region = 'Busan';
      } else if (addr.includes('인천')) {
        region = 'Incheon';
      } else if (addr.includes('경주')) {
        region = 'Gyeongju';
      } else if (addr.includes('전주')) {
        region = 'Jeonju';
      } else if (addr.includes('강원')) {
        region = 'Gangwon';
      } else if (addr.includes('경기')) {
        region = 'Gyeonggi';
      }

      // Resolve Photo: First check curated authentic photo map, otherwise use high-res aesthetic category photo
      let photoUrl = getCuratedAuthenticPhoto(placeName, placeName);
      if (!photoUrl) {
        if (isVeggie) {
          photoUrl = KAKAO_CATEGORY_PHOTOS.vegetarian_food;
        } else if (item.category_group_code === 'CE7') {
          photoUrl = KAKAO_CATEGORY_PHOTOS.cafe;
        } else if (item.category_group_code === 'FD6') {
          photoUrl = KAKAO_CATEGORY_PHOTOS.restaurant;
        } else {
          photoUrl = KAKAO_CATEGORY_PHOTOS[category] || KAKAO_CATEGORY_PHOTOS.default;
        }
      }

      const tags = ['Kakao Live', item.category_group_name || 'Spot'];
      if (isVeggie) {
        tags.push('Vegetarian', 'Veggie Friendly');
      }

      spots.push({
        id: `kakao-${item.id}`,
        name: item.place_name,
        nameDe: item.place_name,
        nameKr: item.place_name,
        category,
        region,
        district: item.address_name.split(' ')[1] || 'Kakao Place',
        lat: spotLat,
        lng: spotLng,
        rating: 4.8,
        reviewCount: 95,
        description: `${item.category_name} · ${item.road_address_name || item.address_name}`,
        descriptionDe: `${item.category_name} · ${item.road_address_name || item.address_name}`,
        photoTips: `Offizieller Kakao-Spot: ${item.place_name}. Siehe Öffnungszeiten & Fotos via Kakao Map.`,
        photoTipsDe: `Offizieller Kakao-Spot: ${item.place_name}. Siehe Öffnungszeiten & Fotos via Kakao Map.`,
        bestTime: 'Tagsüber',
        bestTimeDe: 'Tagsüber',
        imageUrl: photoUrl,
        subwayInfo: item.road_address_name || item.address_name,
        feeInfo: 'Siehe Kakao Info',
        feeInfoDe: 'Siehe Kakao Info',
        tags,
        isMustSee: false,
        isVegetarianOnly: isVeggie,
        dietType: isVeggie ? 'vegan' : undefined,
        dietDetailsDe: isVeggie ? dietDetailsDe : undefined,
        dietDetailsEn: isVeggie ? dietDetailsEn : undefined,
      });
    }

    return spots;
  } catch (err) {
    console.warn('Kakao Places query error:', err);
    return [];
  }
}
